import { List } from "immutable";

/** An opening/annotation named in the blocked-save flash — the kind groups it, the label names it. */
export interface BlockedLocationItem {
  kind: "window" | "door";
  label: string;
}

/** Offenders past this many are summarized as "(and N more)" rather than listed. */
const BLOCKED_LOCATION_LIST_LIMIT = 5;

/**
 * The blocked-save message naming the openings that still need a Location.
 *
 * Single line by necessity — `FlashAlert` renders the message in one `<P>`, so newlines collapse.
 *
 * @param {List<BlockedLocationItem>} items the blocking openings, windows before doors
 * @returns {string} e.g. `Location is required before saving. Windows: F1, F2; Doors: D1 (and 3 more)`
 */
export const blockedLocationMessage = (items: List<BlockedLocationItem>): string => {
  const shown = items.take(BLOCKED_LOCATION_LIST_LIMIT);
  const groups = (["window", "door"] as const)
    .map((kind) => {
      const labels = shown.filter((item) => item.kind === kind).map((item) => item.label);
      return labels.isEmpty() ? "" : `${kind === "window" ? "Windows" : "Doors"}: ${labels.join(", ")}`;
    })
    .filter(Boolean)
    .join("; ");
  const remaining = items.size - shown.size;

  return `Location is required before saving. ${groups}${remaining > 0 ? ` (and ${remaining} more)` : ""}`;
};

// Converts a pitch ratio (rise over 12) to the roof pitch angle in degrees
export const calculateRoofPitchAngle = (pitch: number) => {
  const angleRadians = Math.atan(pitch / 12);
  const angleDegrees = angleRadians * (180 / Math.PI);
  return parseFloat(angleDegrees.toFixed(2));
};

// Converts a roof pitch angle (in degrees) back to the pitch ratio (rise over run).
export const calculateRoofPitch = (degrees: number) => {
  const inRadians = Math.tan(degrees * (Math.PI / 180));
  const unrounded = inRadians * 12;
  return parseFloat((Math.round(unrounded * 2) / 2).toFixed(1));
};

// Adjusts the roof area based on the pitch.
export const calculateRoofAreaFromPitch = (area: number, pitch: number) => {
  const newLength = Math.sqrt(Math.pow(pitch, 2) + Math.pow(12, 2));
  const factor = newLength / 12;

  return Math.round(area * factor);
};
